import { useState, FormEvent } from 'react';
import { API_BASE } from '../lib/auth';
import { setStoredSessionId } from '../lib/api';

/**
 * Sprint 9.C — Login PRO con form completo (NON più redirect a SPA mobile).
 *
 * 2 blocchi distanziati:
 *  1. Magic link rapido (1 sola email) — disabilitato per multi-ruolo
 *  2. Email + password (multi-ruolo supported)
 *
 * Origin: pro-dietamenu.schedy.app
 * Fetch:  https://dietamenu.schedy.app/api/auth/* (cross-origin con credentials)
 *
 * Il browser invia automatically `Origin: https://pro-dietamenu.schedy.app`
 * → worker `inferRoleFromHost(host, origin)` ritorna 'dietologist' → autenticazione
 * bound al role pro anche se l'utente ha multi-ruolo con stessa email.
 */
export default function Login() {
  // Stato form
  const [magicEmail, setMagicEmail] = useState('');
  const [magicStatus, setMagicStatus] = useState<{ type: 'info' | 'warn' | 'err'; msg: string } | null>(null);
  const [magicSending, setMagicSending] = useState(false);

  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState<string | null>(null);
  const [logging, setLogging] = useState(false);

  const handleMagicSubmit = (e: FormEvent) => {
    e.preventDefault();
    const email = magicEmail.trim();
    if (!email) {
      setMagicStatus({ type: 'warn', msg: 'Inserisci prima la tua email' });
      return;
    }
    setMagicSending(true);
    setMagicStatus({ type: 'info', msg: 'Invio in corso…' });
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/auth/magic/request`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        });
        if (res.status === 429) {
          setMagicStatus({ type: 'warn', msg: 'Troppe richieste. Riprova tra 10 minuti.' });
          return;
        }
        if (!res.ok) {
          setMagicStatus({ type: 'err', msg: 'Errore server. Riprova.' });
          return;
        }
        const data = await res.json().catch(() => ({} as { multi_role_hint?: boolean }));
        if (data && data.multi_role_hint) {
          setMagicStatus({
            type: 'warn',
            msg: 'Account multi-ruolo: usa email e password qui sotto, sul portale corretto.',
          });
          return;
        }
        setMagicStatus({
          type: 'info',
          msg: 'Email inviata. Controlla la casella (anche spam) e clicca il link.',
        });
      } catch {
        setMagicStatus({ type: 'err', msg: 'Errore di rete. Riprova.' });
      } finally {
        setMagicSending(false);
      }
    })();
  };

  const handleLoginSubmit = (e: FormEvent) => {
    e.preventDefault();
    setLoginError(null);
    const email = loginEmail.trim();
    if (!email || !loginPassword) {
      setLoginError('Inserisci email e password');
      return;
    }
    setLogging(true);
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/auth/login`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          // Origin pro-dietamenu.* discrimina lato server → role='dietologist'.
          // Aggiungiamo anche body.role esplicito come safety net per future
          // configurazioni proxy/CORS che strippassero Origin.
          body: JSON.stringify({ email, password: loginPassword, role: 'dietologist' }),
        });
        if (res.status === 401) {
          setLoginError('Credenziali non valide');
          return;
        }
        if (res.status === 403) {
          const data = await res.json().catch(() => ({} as { error?: string }));
          setLoginError(data.error || 'Account non accessibile');
          return;
        }
        if (!res.ok) {
          setLoginError('Errore server. Riprova.');
          return;
        }
        const data = await res.json() as { user: { role: string }; session_id?: string };
        if (data.user.role !== 'dietologist') {
          setLoginError("Questo account non è un'utenza pro. Usa il portale paziente su dietamenu.schedy.app");
          return;
        }
        // Sprint 9.D — salva session_id in localStorage per X-Session-Id header fallback
        // (Chrome CHIPS / third-party cookie blocking partition i cookie cross-subdomain).
        if (data.session_id) {
          setStoredSessionId(data.session_id);
        }
        // Redirect: dashboard o onboarding (server-provided redirect non disponibile su login).
        window.location.href = '/dashboard';
      } catch {
        setLoginError('Errore di rete. Riprova.');
      } finally {
        setLogging(false);
      }
    })();
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '32px 20px',
        background: 'var(--paper, #fafaf6)',
        fontFamily: 'var(--sans)',
      }}
    >
      <div style={{ width: '100%', maxWidth: 440 }}>
        {/* Brand */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div
            style={{
              fontFamily: 'var(--mono, monospace)',
              fontSize: 10.5,
              fontWeight: 900,
              letterSpacing: 1.6,
              color: 'var(--mute, #666)',
              textTransform: 'uppercase',
            }}
          >
            Studio Pro
          </div>
          <h1
            style={{
              fontFamily: 'var(--display)',
              fontWeight: 800,
              fontSize: 36,
              letterSpacing: -1,
              margin: '8px 0 0',
              lineHeight: 1.05,
              color: 'var(--ink, #0f1410)',
            }}
          >
            Accedi al portale
          </h1>
          <p style={{ color: 'var(--mute, #666)', fontSize: 14, marginTop: 10 }}>
            Portale professionale DietAmenu. Per pazienti: dietamenu.schedy.app
          </p>
        </div>

        {/* ─── BLOCCO 1: Magic link ─── */}
        <section
          style={{
            background: 'var(--white, #fff)',
            border: '2.5px solid var(--line, #0f1410)',
            borderRadius: 16,
            boxShadow: '4px 4px 0 var(--line, #0f1410)',
            padding: 24,
            marginBottom: 32,
          }}
        >
          <div
            style={{
              fontSize: 10.5,
              fontWeight: 900,
              letterSpacing: 1.6,
              textTransform: 'uppercase',
              color: 'var(--mute)',
              marginBottom: 12,
              fontFamily: 'var(--mono, monospace)',
            }}
          >
            Accesso rapido · Magic link
          </div>
          <p style={{ fontSize: 13, color: 'var(--mute)', margin: '0 0 16px', lineHeight: 1.4 }}>
            Inserisci la tua email registrata e ti mandiamo un link per entrare senza password.
          </p>
          <form onSubmit={handleMagicSubmit}>
            <label
              style={{
                display: 'block',
                fontSize: 11,
                fontWeight: 900,
                letterSpacing: 1.2,
                margin: '0 4px 6px',
              }}
            >
              EMAIL REGISTRATA
            </label>
            <input
              type="email"
              autoComplete="email"
              placeholder="es. mario@email.com"
              value={magicEmail}
              onChange={(e) => setMagicEmail(e.target.value)}
              style={{
                width: '100%',
                padding: '14px 16px',
                fontSize: 15,
                fontWeight: 700,
                background: 'var(--paper, #fafaf6)',
                border: '2.5px solid var(--line)',
                borderRadius: 12,
                outline: 'none',
                marginBottom: 14,
                boxSizing: 'border-box',
              }}
            />
            <button
              type="submit"
              disabled={magicSending}
              style={{
                width: '100%',
                padding: 14,
                background: 'var(--green, #0b8a3e)',
                color: '#fff',
                border: '2.5px solid var(--line)',
                borderRadius: 12,
                boxShadow: '3px 3px 0 var(--line)',
                fontSize: 14,
                fontWeight: 900,
                letterSpacing: 0.8,
                textTransform: 'uppercase',
                cursor: magicSending ? 'wait' : 'pointer',
                opacity: magicSending ? 0.6 : 1,
              }}
            >
              {magicSending ? 'Invio…' : 'Inviami il link'}
            </button>
            {magicStatus && (
              <div
                role="status"
                style={{
                  marginTop: 12,
                  padding: '0 4px',
                  fontSize: 13,
                  fontWeight: magicStatus.type === 'warn' ? 700 : 500,
                  color:
                    magicStatus.type === 'err'
                      ? 'var(--red, #c53030)'
                      : magicStatus.type === 'warn'
                      ? 'var(--orange, #c75c00)'
                      : 'var(--mute)',
                  textAlign: 'center',
                  lineHeight: 1.4,
                }}
              >
                {magicStatus.msg}
              </div>
            )}
          </form>
        </section>

        {/* ─── Separator OPPURE ─── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, margin: '32px 0' }}>
          <div style={{ flex: 1, height: 2, background: 'var(--line, #0f1410)' }} />
          <div
            style={{
              fontSize: 11,
              fontWeight: 900,
              letterSpacing: 2,
              color: 'var(--mute)',
              textTransform: 'uppercase',
            }}
          >
            oppure
          </div>
          <div style={{ flex: 1, height: 2, background: 'var(--line, #0f1410)' }} />
        </div>

        {/* ─── BLOCCO 2: Email + password ─── */}
        <section
          style={{
            background: 'var(--white, #fff)',
            border: '2.5px solid var(--line, #0f1410)',
            borderRadius: 16,
            boxShadow: '4px 4px 0 var(--line, #0f1410)',
            padding: 24,
          }}
        >
          <div
            style={{
              fontSize: 10.5,
              fontWeight: 900,
              letterSpacing: 1.6,
              textTransform: 'uppercase',
              color: 'var(--mute)',
              marginBottom: 12,
              fontFamily: 'var(--mono, monospace)',
            }}
          >
            Account multi-ruolo · Email e password
          </div>
          <p style={{ fontSize: 13, color: 'var(--mute)', margin: '0 0 16px', lineHeight: 1.4 }}>
            Se hai più ruoli associati alla tua email, accedi qui con email e password. Sarà loggato come <strong>pro</strong> (questo portale).
          </p>
          {loginError && (
            <div
              role="alert"
              style={{
                padding: '12px 14px',
                background: 'var(--orange, #fff0e1)',
                color: 'var(--ink, #0f1410)',
                border: '2px solid var(--line)',
                borderRadius: 12,
                marginBottom: 14,
                fontSize: 13,
                fontWeight: 800,
              }}
            >
              {loginError}
            </div>
          )}
          <form onSubmit={handleLoginSubmit}>
            <label
              style={{
                display: 'block',
                fontSize: 11,
                fontWeight: 900,
                letterSpacing: 1.2,
                margin: '0 4px 6px',
              }}
            >
              EMAIL
            </label>
            <input
              type="email"
              autoComplete="email"
              value={loginEmail}
              onChange={(e) => setLoginEmail(e.target.value)}
              style={{
                width: '100%',
                padding: '14px 16px',
                fontSize: 15,
                fontWeight: 700,
                background: 'var(--paper, #fafaf6)',
                border: '2.5px solid var(--line)',
                borderRadius: 12,
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
            <label
              style={{
                display: 'block',
                fontSize: 11,
                fontWeight: 900,
                letterSpacing: 1.2,
                margin: '18px 4px 6px',
              }}
            >
              PASSWORD
            </label>
            <input
              type="password"
              autoComplete="current-password"
              value={loginPassword}
              onChange={(e) => setLoginPassword(e.target.value)}
              style={{
                width: '100%',
                padding: '14px 16px',
                fontSize: 15,
                fontWeight: 700,
                background: 'var(--paper, #fafaf6)',
                border: '2.5px solid var(--line)',
                borderRadius: 12,
                outline: 'none',
                marginBottom: 18,
                boxSizing: 'border-box',
              }}
            />
            <button
              type="submit"
              disabled={logging}
              style={{
                width: '100%',
                padding: 14,
                background: 'var(--ink, #0f1410)',
                color: '#fff',
                border: '2.5px solid var(--line)',
                borderRadius: 12,
                boxShadow: '3px 3px 0 var(--line)',
                fontSize: 14,
                fontWeight: 900,
                letterSpacing: 0.8,
                textTransform: 'uppercase',
                cursor: logging ? 'wait' : 'pointer',
                opacity: logging ? 0.6 : 1,
              }}
            >
              {logging ? 'Accesso…' : 'Accedi come pro'}
            </button>
          </form>
        </section>

        <div style={{ textAlign: 'center', marginTop: 24, fontSize: 13, color: 'var(--mute)' }}>
          Non hai un account pro?{' '}
          <a
            href={`${API_BASE}/?screen=auth-register&role=dietologist`}
            style={{ color: 'var(--green, #0b8a3e)', fontWeight: 700, textDecoration: 'underline' }}
          >
            Registrati
          </a>
        </div>
      </div>
    </div>
  );
}
